/**
 * ContactsView - Team contacts list with call/video buttons
 */

'use client';

import React, { useMemo } from 'react';
import {User, 
  Users, 
  Phone, 
  Video,
  Search,
  BookUser} from 'lucide-react';
import type { ContactsViewProps, OperationsUser } from '../types';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

export function ContactsView({
  users,
  loading,
  searchQuery,
  onSearchChange,
  onStartCall,
  onSelectContact,
  isUserOnline,
  getUserLastSeen,
  isDark,
  primaryColor,
  currentUserId,
  canAccessPhoneContacts = false,
  onOpenPhoneContacts,
  hideSearchBar = false,
  sortMode = 'name_asc',
}: ContactsViewProps) {
  // Filter users by search
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) ||
           (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const sortedUsers = useMemo(() => {
    const base = [...filteredUsers];
    if (sortMode === 'online_first') {
      return base.sort((a, b) => {
        const oa = isUserOnline(a) ? 0 : 1;
        const ob = isUserOnline(b) ? 0 : 1;
        if (oa !== ob) return oa - ob;
        return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, undefined, {
          sensitivity: 'base',
        });
      });
    }
    return base.sort((a, b) => {
      const cmp = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, undefined, {
        sensitivity: 'base',
      });
      return sortMode === 'name_desc' ? -cmp : cmp;
    });
  }, [filteredUsers, sortMode, isUserOnline]);
  
  // Get online users
  const onlineUsers = sortedUsers.filter(u => isUserOnline(u));

  // Separate own vs partner contacts
  const ownContacts = sortedUsers.filter(u => !u.is_partner);
  const partnerContacts = sortedUsers.filter(u => u.is_partner);
  // Group partner contacts by organization
  const partnerGrouped = partnerContacts.reduce((acc, u) => {
    const key = u.partner_client_name || 'Partner';
    if (!acc[key]) acc[key] = [];
    acc[key].push(u);
    return acc;
  }, {} as Record<string, OperationsUser[]>);
  
  return (
    <div className="p-3 sm:p-4 space-y-4">
      {!hideSearchBar ? (
        <div className="flex items-center gap-2">
          <div className={`relative min-w-0 flex-1 ${isDark ? '' : ''}`}>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={`w-full rounded-lg border py-2.5 pl-9 pr-3 text-sm focus:border-transparent focus:ring-2 ${
                isDark
                  ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                  : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
              }`}
              style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
            />
          </div>
          {canAccessPhoneContacts && onOpenPhoneContacts ? (
            <button
              type="button"
              onClick={onOpenPhoneContacts}
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                isDark
                  ? 'border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Search device contacts"
            >
              <BookUser className="h-5 w-5" />
            </button>
          ) : null}
        </div>
      ) : null}
      
      {/* Online Now Section */}
      {onlineUsers.length > 0 && (
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Online Now ({onlineUsers.length})
          </p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {onlineUsers.slice(0, 6).map((opUser) => (
              <div 
                key={opUser.id} 
                className="flex flex-col items-center min-w-[60px] cursor-pointer"
                onClick={() => onStartCall(opUser, 'voice')}
              >
                <div className="relative">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden ${
                    isDark ? 'bg-gray-700' : 'bg-gray-200'
                  }`}>
                    {opUser.avatar_url ? (
                      <img src={opUser.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    )}
                  </div>
                  <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                    isDark ? 'border-gray-900' : 'border-white'
                  } bg-green-500`} />
                </div>
                <p className={`text-xs mt-1 text-center truncate max-w-[60px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {opUser.first_name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* All Contacts Section */}
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          All Contacts ({sortedUsers.length})
        </p>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
          </div>
        ) : sortedUsers.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No team members found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedUsers.map((opUser, idx) => (
              <div
                key={opUser.id || `user-${idx}`}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  isDark ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-50 hover:bg-gray-100'
                } transition-colors`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Avatar */}
                  <button
                    onClick={() => onSelectContact?.(opUser)}
                    className="relative group/avatar cursor-pointer"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden transition-transform group-hover/avatar:scale-110 ${
                      isDark ? 'bg-gray-700' : 'bg-gray-200'
                    }`}>
                      {opUser.avatar_url ? (
                        <img src={opUser.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      )}
                    </div>
                    {/* Online indicator */}
                    {opUser.show_when_online !== false && (
                      <span 
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                          isDark ? 'border-gray-800' : 'border-gray-50'
                        } ${isUserOnline(opUser) ? 'bg-green-500' : 'bg-gray-400'}`}
                        title={getUserLastSeen(opUser) || 'Offline'}
                      />
                    )}
                    {/* Role badge */}
                    {['owner', 'admin', 'supervisor'].includes(opUser.module_role?.toLowerCase() || '') && (
                      <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${
                        opUser.module_role?.toLowerCase() === 'owner' ? 'bg-amber-500 text-white' :
                        opUser.module_role?.toLowerCase() === 'admin' ? 'bg-purple-500 text-white' :
                        'bg-blue-500 text-white'
                      }`} title={opUser.module_role}>
                        {opUser.module_role?.toLowerCase() === 'owner' ? '👑' : '⭐'}
                      </div>
                    )}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {opUser.first_name} {opUser.last_name}
                    </p>
                    <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {opUser.is_partner 
                        ? opUser.partner_client_name || 'Partner' 
                        : getUserLastSeen(opUser) || (opUser.is_field_worker ? 'Field Worker' : opUser.module_role || 'Team Member')}
                    </p>
                  </div>
                  
                  {/* Partner badge */}
                  {opUser.is_partner && (
                    <div className="mx-2 flex-shrink-0">
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-500 text-white" title={`Partner: ${opUser.partner_client_name}`}>
                        Partner
                      </span>
                    </div>
                  )}

                  {/* Client badges */}
                  {opUser.client_names && opUser.client_names.length > 0 && (
                    <div className="flex flex-col gap-0.5 mx-2 flex-shrink-0">
                      {opUser.client_names.slice(0, 3).map((clientName, cidx) => {
                        const badgeColors = [
                          'bg-blue-500 text-white',
                          'bg-purple-500 text-white',
                          'bg-orange-500 text-white'
                        ];
                        return (
                          <span 
                            key={cidx}
                            className={`text-[9px] px-1.5 py-0.5 rounded truncate max-w-[80px] ${badgeColors[cidx]}`}
                            title={clientName}
                          >
                            {clientName.length > 12 ? clientName.substring(0, 12) + '…' : clientName}
                          </span>
                        );
                      })}
                      {opUser.client_names.length > 3 && (
                        <span className={`text-[9px] px-1.5 py-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          +{opUser.client_names.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Call buttons */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => onStartCall(opUser, 'voice')}
                    className="w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center active:scale-95 transition-all"
                  >
                    <Phone className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => onStartCall(opUser, 'video')}
                    className="w-9 h-9 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center active:scale-95 transition-all"
                  >
                    <Video className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactsView;
